'use client';
/* eslint @typescript-eslint/no-explicit-any: 0 */ // --> OFF

import { createContext, useContextSelector } from 'use-context-selector';
import { useCallback, useEffect, useRef, useState } from 'react';

type LocalStorageContext = {
  updateDraft: (val: any) => void;
  removeDraft: () => void;
  draftCreation: any;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  removeFavorite: (id: string) => void;
  addFavorite: (id: string) => void;
  clearLocalFavorites: () => void;
};

const defaultNoop = (_?: string) => {};

export const DRAFT_PRODUCT_ID_STORAGE_KEY = 'doskatv-draft-product-id';

export const LocalStorageContext = createContext<LocalStorageContext>({
  updateDraft: defaultNoop,
  removeDraft: defaultNoop,
  draftCreation: null,
  favorites: [],
  toggleFavorite: defaultNoop,
  removeFavorite: defaultNoop,
  addFavorite: defaultNoop,
  clearLocalFavorites: defaultNoop,
});

export const LocalStorageContextProvider = ({ children }) => {
  const [draftCreation, setDraftCreationPure] = useState(null);
  const [favorites, setFavoritesPure] = useState<string[]>([]);
  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;

  useEffect(() => {
    const draftProductLS = window.localStorage.getItem('doskatv-draft-product');
    if (draftProductLS) {
      setDraftCreationPure(JSON.parse(draftProductLS));
    }
    const favoritesLS = window.localStorage.getItem('doska-tv-favorites');

    if (favoritesLS && favoritesLS !== 'undefined') {
      setFavoritesPure(JSON.parse(favoritesLS));
    }
  }, []);

  const setDraftCreation = useCallback((newVal: any) => {
    if (newVal === null) {
      setDraftCreationPure(null);
      window.localStorage.removeItem('doskatv-draft-product');
      window.localStorage.removeItem(DRAFT_PRODUCT_ID_STORAGE_KEY);
      return;
    }
    const valueWithCreatedAt = {
      ...newVal,
      createdAt: new Date().toISOString(),
    };
    setDraftCreationPure(valueWithCreatedAt);
    window.localStorage.setItem('doskatv-draft-product', JSON.stringify(valueWithCreatedAt));
  }, []);

  const setFavorites = useCallback((newVal: string[]) => {
    favoritesRef.current = newVal;
    setFavoritesPure(newVal);
    window.localStorage.setItem('doska-tv-favorites', JSON.stringify(newVal));
  }, []);

  const updateDraft = useCallback(
    (value) => {
      if (value.name || value.description || value.price || value.photos.length) {
        setDraftCreation(value);
      } else {
        setDraftCreation(null);
      }
    },
    [setDraftCreation],
  );

  const removeDraft = useCallback(() => {
    setDraftCreation(null);
  }, [setDraftCreation]);

  const toggleFavorite = useCallback(
    (newId: string) => {
      const current = favoritesRef.current;
      if (current.includes(newId)) {
        setFavorites(current.filter((productId) => productId !== newId));
      } else {
        setFavorites([...current, newId]);
      }
    },
    [setFavorites],
  );

  const removeFavorite = useCallback(
    (newId: string) => {
      setFavorites(favoritesRef.current.filter((productId) => productId !== newId));
    },
    [setFavorites],
  );

  const addFavorite = useCallback(
    (newId: string) => {
      setFavorites([...favoritesRef.current, newId]);
    },
    [setFavorites],
  );

  const clearLocalFavorites = useCallback(() => {
    setFavorites([]);
  }, [setFavorites]);

  const value = {
    updateDraft,
    removeDraft,
    draftCreation,
    favorites,
    toggleFavorite,
    removeFavorite,
    addFavorite,
    clearLocalFavorites,
  };

  return (
    <>
      <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>
    </>
  );
};

export const useUpdateDraft = () => useContextSelector(LocalStorageContext, (value) => value.updateDraft);
export const useRemoveDraft = () => useContextSelector(LocalStorageContext, (value) => value.removeDraft);
export const useCreationDraft = () => useContextSelector(LocalStorageContext, (value) => value.draftCreation);

export const useToggleFavorite = () => useContextSelector(LocalStorageContext, (value) => value.toggleFavorite);
export const useFavorites = () => useContextSelector(LocalStorageContext, (value) => value.favorites);
export const useIsLocalFavorite = (productId: string) =>
  useContextSelector(LocalStorageContext, (value) => value.favorites.includes(productId));
export const useClearLocalFavorites = () =>
  useContextSelector(LocalStorageContext, (value) => value.clearLocalFavorites);
